/**
 * @fileoverview Profile page component for user account settings.
 * Displays user information and provides access to theme settings, password reset,
 * and budget management (edit/delete owned budgets).
 */

import { useState, useEffect } from 'react';
import { ColorThemeSelector } from '../ThemeProvider/ColorThemeSelector';
import EditBudgetModal from './EditBudgetModal';
import DeleteBudgetConfirmation from './DeleteBudgetConfirmation';
import CeaseBudgetModal from './CeaseBudgetModal';
import styles from './Profile.module.css';
import budgetStyles from './BudgetManagement.module.css';

/**
 * Profile Component
 *
 * User profile and settings page including:
 * - User information display
 * - Password reset functionality (hidden for social login users)
 * - Theme customization (color theme and dark mode)
 * - Budget management (edit/delete owned budgets)
 *
 * Props:
 * - userName (string): User's display name
 * - userEmail (string): User's email address
 * - user (object): Auth0 user object (to check authentication method)
 * - getAccessTokenSilently (function): Auth0 function to get access token
 * - authUser (object): Auth user object with budget information
 * - onBudgetDeleted (function): Callback when a budget is deleted
 */
function Profile({ userName, userEmail, user, getAccessTokenSilently, authUser, onBudgetDeleted }) {
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);

    // Budget management state
    const [ownedBudgets, setOwnedBudgets] = useState([]);
    const [budgetsLoading, setBudgetsLoading] = useState(true);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [ceaseModalOpen, setCeaseModalOpen] = useState(false);
    const [selectedBudget, setSelectedBudget] = useState(null);
    const [budgetError, setBudgetError] = useState(null);

    // Check if user is using social login (Google, etc.)
    // Auth0 user sub format: "google-oauth2|123456" for Google users, "auth0|123456" for database users
    const isSocialLogin = user?.sub && !user.sub.startsWith('auth0|');

    // Load owned budgets
    useEffect(() => {
        const loadOwnedBudgets = async () => {
            try {
                setBudgetsLoading(true);
                const token = await getAccessTokenSilently();
                const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
                const response = await fetch(`${API_BASE_URL}/budgets/owned`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (!response.ok) {
                    throw new Error('Failed to load owned budgets');
                }

                const data = await response.json();
                setOwnedBudgets(data);
            } catch (err) {
                setBudgetError(err.message);
            } finally {
                setBudgetsLoading(false);
            }
        };

        loadOwnedBudgets();
    }, [getAccessTokenSilently]);

    const handlePasswordReset = async () => {
        try {
            setLoading(true);
            setMessage(null);
            setError(null);

            const token = await getAccessTokenSilently();
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
            const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email: userEmail })
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to send password reset email');
            }

            setMessage('Password reset email sent! Check your inbox.');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleEditBudget = (budget) => {
        setSelectedBudget(budget);
        setBudgetError(null);
        setEditModalOpen(true);
    };

    const handleDeleteBudget = (budget) => {
        setSelectedBudget(budget);
        setDeleteModalOpen(true);
    };

    const handleSaveBudget = async (budgetKey, newName, newSingleBankMode) => {
        try {
            setBudgetError(null);
            const token = await getAccessTokenSilently();
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

            // Update budget name
            const nameResponse = await fetch(`${API_BASE_URL}/budgets/${budgetKey}/name`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ budgetName: newName })
            });

            if (!nameResponse.ok) {
                const data = await nameResponse.json();
                throw new Error(data.error || 'Failed to update budget name');
            }

            const nameResult = await nameResponse.json();

            // Update single bank mode
            const modeResponse = await fetch(`${API_BASE_URL}/budgets/${budgetKey}/single-bank-mode`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ enabled: newSingleBankMode })
            });

            if (!modeResponse.ok) {
                const data = await modeResponse.json();
                // Include details if available for validation errors
                const errorMessage = data.details
                    ? `${data.error}\n\n${data.details}`
                    : (data.error || 'Failed to update single bank mode');
                throw new Error(errorMessage);
            }

            const modeResult = await modeResponse.json();

            // Update local state
            setOwnedBudgets(prev =>
                prev.map(b => b.budgetKey === budgetKey
                    ? { ...b, budgetName: nameResult.budgetName, singleBankMode: modeResult.singleBankMode }
                    : b
                )
            );

            // Refresh authUser to update budget list in dropdown
            if (authUser?.refreshUser) {
                await authUser.refreshUser();
            }

            // Reload the owned budgets list to show updated values
            const budgetsResponse = await fetch(`${API_BASE_URL}/budgets/owned`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (budgetsResponse.ok) {
                const data = await budgetsResponse.json();
                setOwnedBudgets(data);
            }

            setEditModalOpen(false);
            setSelectedBudget(null);
        } catch (err) {
            setBudgetError(err.message);
        }
    };

    const handleConfirmDelete = async (budgetKey) => {
        try {
            const token = await getAccessTokenSilently();
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
            const response = await fetch(`${API_BASE_URL}/budgets/${budgetKey}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to delete budget');
            }

            // Remove from local state
            setOwnedBudgets(prev => prev.filter(b => b.budgetKey !== budgetKey));

            setDeleteModalOpen(false);
            setSelectedBudget(null);

            // Notify parent component to handle budget switching
            if (onBudgetDeleted) {
                onBudgetDeleted(budgetKey);
            }
        } catch (err) {
            setBudgetError(err.message);
            setDeleteModalOpen(false);
        }
    };

    const handleCeaseBudget = (budget) => {
        setSelectedBudget(budget);
        setBudgetError(null);
        setCeaseModalOpen(true);
    };

    const handleConfirmCease = async (budgetKey, ceaseDate) => {
        try {
            setBudgetError(null);
            const token = await getAccessTokenSilently();
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

            const response = await fetch(`${API_BASE_URL}/budgets/${budgetKey}/cease`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ ceaseDate })
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to cease budget');
            }

            // Update local state
            setOwnedBudgets(prev =>
                prev.map(b => b.budgetKey === budgetKey
                    ? { ...b, ceaseDate }
                    : b
                )
            );

            // Refresh authUser to update budget list in dropdown
            if (authUser?.refreshUser) {
                await authUser.refreshUser();
            }

            setCeaseModalOpen(false);
            setSelectedBudget(null);
        } catch (err) {
            setBudgetError(err.message);
        }
    };

    const handleUnceaseBudget = async (budgetKey) => {
        try {
            setBudgetError(null);
            const token = await getAccessTokenSilently();
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

            const response = await fetch(`${API_BASE_URL}/budgets/${budgetKey}/uncease`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to uncease budget');
            }

            // Update local state - set ceaseDate to null
            setOwnedBudgets(prev =>
                prev.map(b => b.budgetKey === budgetKey
                    ? { ...b, ceaseDate: null }
                    : b
                )
            );

            // Refresh authUser to update budget list in dropdown
            if (authUser?.refreshUser) {
                await authUser.refreshUser();
            }
        } catch (err) {
            setBudgetError(err.message);
        }
    };

    return (
        <div className={styles.container}>
            <h2 className={styles.pageTitle}>Profile & Settings</h2>

            {/* User Information Section */}
            <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Account Information</h3>
                <div className={styles.card}>
                    <div className={styles.infoRow}>
                        <span className={styles.label}>Name:</span>
                        <span className={styles.value}>{userName || 'Not set'}</span>
                    </div>
                    <div className={styles.infoRow}>
                        <span className={styles.label}>Email:</span>
                        <span className={styles.value}>{userEmail}</span>
                    </div>
                </div>
            </section>

            {/* Password Section - Only show for database users (not social login) */}
            {!isSocialLogin && (
                <section className={styles.section}>
                    <h3 className={styles.sectionTitle}>Password</h3>
                    <div className={styles.card}>
                        <p className={styles.description}>
                            Click the button below to receive a password reset email from Auth0.
                            You'll be able to set a new password securely.
                        </p>
                        <button
                            onClick={handlePasswordReset}
                            disabled={loading}
                            className={styles.resetButton}
                        >
                            {loading ? 'Sending...' : 'Change Password'}
                        </button>
                        {message && (
                            <div className={styles.successMessage}>
                                {message}
                            </div>
                        )}
                        {error && (
                            <div className={styles.errorMessage}>
                                {error}
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* Theme Settings Section */}
            <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Color Theme</h3>
                <div className={styles.card}>
                    <div className={styles.settingRow}>
                        <div className={styles.settingInfo}>
                            <span className={styles.settingLabel}>Theme</span>
                            <span className={styles.settingDescription}>
                                Choose from 8 color themes (4 light, 4 dark)
                            </span>
                        </div>
                        <div className={styles.settingControl}>
                            <ColorThemeSelector />
                        </div>
                    </div>
                </div>
            </section>

            {/* Budget Management Section */}
            <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Budgets You Own</h3>
                <div className={styles.card}>
                    {budgetsLoading ? (
                        <p className={budgetStyles.noBudgets}>Loading budgets...</p>
                    ) : budgetError ? (
                        <div className={styles.errorMessage}>{budgetError}</div>
                    ) : ownedBudgets.length === 0 ? (
                        <p className={budgetStyles.noBudgets}>You don't own any budgets</p>
                    ) : (
                        <div className={budgetStyles.budgetList}>
                            {ownedBudgets.map(budget => (
                                <div key={budget.budgetKey} className={budgetStyles.budgetRow}>
                                    <span className={budgetStyles.budgetName}>
                                        {budget.budgetName}
                                        {budget.ceaseDate && (
                                            <span className={budgetStyles.ceasedBadge}>CEASED</span>
                                        )}
                                    </span>
                                    <div className={budgetStyles.budgetActions}>
                                        {budget.ceaseDate ? (
                                            <button
                                                onClick={() => handleUnceaseBudget(budget.budgetKey)}
                                                className={budgetStyles.unceaseButton}
                                            >
                                                Restore
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => handleCeaseBudget(budget)}
                                                className={budgetStyles.ceaseButton}
                                            >
                                                Cease
                                            </button>
                                        )}
                                        <button
                                            onClick={() => handleEditBudget(budget)}
                                            className={budgetStyles.editButton}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDeleteBudget(budget)}
                                            className={budgetStyles.deleteButton}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Edit Budget Modal */}
            <EditBudgetModal
                isOpen={editModalOpen}
                budgetKey={selectedBudget?.budgetKey}
                currentName={selectedBudget?.budgetName || ''}
                currentSingleBankMode={selectedBudget?.singleBankMode || false}
                onClose={() => {
                    setEditModalOpen(false);
                    setSelectedBudget(null);
                    setBudgetError(null);
                }}
                onSave={handleSaveBudget}
                error={budgetError}
            />

            {/* Delete Budget Confirmation */}
            <DeleteBudgetConfirmation
                isOpen={deleteModalOpen}
                budgetKey={selectedBudget?.budgetKey}
                budgetName={selectedBudget?.budgetName || ''}
                onClose={() => {
                    setDeleteModalOpen(false);
                    setSelectedBudget(null);
                }}
                onConfirmDelete={handleConfirmDelete}
            />

            {/* Cease Budget Modal */}
            <CeaseBudgetModal
                isOpen={ceaseModalOpen}
                budgetKey={selectedBudget?.budgetKey}
                budgetName={selectedBudget?.budgetName || ''}
                onClose={() => {
                    setCeaseModalOpen(false);
                    setSelectedBudget(null);
                    setBudgetError(null);
                }}
                onConfirm={handleConfirmCease}
                error={budgetError}
            />
        </div>
    );
}

export default Profile;
